import { useEffect, useId, useRef, useState } from 'react';
import { getBranchEpoch, onBranchChange } from '../api';
import {
  IconAttendance,
  IconCalendar,
  IconCashier,
  IconClock,
  IconCommission,
  IconCustomers,
  IconDiscount,
  IconGlobe,
  IconHandCash,
  IconHistory,
  IconInfo,
  IconKey,
  IconProduct,
  IconReport,
  IconStore,
  IconTreatment,
} from './icons';

/** Small helpers shared by the screens. */

// Every date and time in this app is the shop's, never the viewer's — see
// src/shopTime.js. Re-exported here so screens keep one import.
export {
  SHOP_TZ, TH_LOCALE, todayIso, nowHhmm, shopInstant, shopInstantIso,
  timeOf, dateOf, dateTimeOf, dateThShort, dayIsoOf, dateTh, dayLabel,
  shiftDate, minutesOfDay,
} from '../shopTime';

export const THB = new Intl.NumberFormat('th-TH', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

export const money = (n) => THB.format(Number(n || 0));

/** The till shows satang — a drawer that is 50 satang out is still out. */
export const money2 = (n) =>
  new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(n || 0));

export const initials = (t) =>
  (t?.nicknameEn || t?.nicknameTh || t?.code || '?').slice(0, 2).toUpperCase();

export const STATUS_TH = {
  pending: 'รอคิว',
  // booking_status — a เว็บจอง request arrives as draft and is confirmed by hand
  draft: 'รอยืนยัน',
  booked: 'ยืนยันแล้ว',
  arrived: 'ถึงร้านแล้ว',
  in_service: 'กำลังนวด',
  no_show: 'ไม่มาตามนัด',
  in_progress: 'กำลังนวด',
  completed: 'เสร็จสิ้น',
  cancelled: 'ยกเลิก',
  unpaid: 'ยังไม่ชำระ',
  partial: 'ชำระบางส่วน',
  paid: 'ชำระแล้ว',
  refunded: 'คืนเงิน',
  working: 'ทำงาน',
  off_duty: 'เลิกงาน',
  on_break: 'พัก',
  scheduled: 'รอเข้างาน',
  absent: 'ขาด',
  leave: 'ลา',
  walk_in: 'Walk-in',
  booking: 'จองล่วงหน้า',
  agency: 'เอเจนซี่',
  online: 'จองออนไลน์',
  cash: 'เงินสด',
  transfer: 'เงินโอน',
  qr_promptpay: 'QR PromptPay',
  credit_card: 'บัตรเครดิต',
  e_wallet: 'E-Wallet',
  package: 'แพ็กเกจ',
  voucher: 'Voucher',
  other: 'อื่น ๆ',
  full_time: 'ประจำ (Full Time)',
  daily: 'รายวัน (Daily)',
  part_time: 'ชั่วคราว (Part Time)',
  freelance: 'ฟรีแลนซ์',
  service: 'บริการ',
  product: 'สินค้า',
  mixed: 'ผสม',
  open: 'เปิดอยู่',
  void: 'ยกเลิก',
};

export const th = (key) => STATUS_TH[key] || key || '-';

export const STATUS_BADGE = {
  completed: 'badge-green',
  paid: 'badge-green',
  working: 'badge-green',
  in_progress: 'badge-amber',
  partial: 'badge-amber',
  pending: 'badge-grey',
  scheduled: 'badge-grey',
  off_duty: 'badge-grey',
  unpaid: 'badge-red',
  cancelled: 'badge-red',
  void: 'badge-red',
};

export function Badge({ value }) {
  return <span className={`badge ${STATUS_BADGE[value] || 'badge-grey'}`}>{th(value)}</span>;
}

export function Spinner() {
  return <div className="spinner" />;
}

const EMPTY_ICONS = {
  appointment: IconCalendar,
  branch: IconStore,
  cashier: IconCashier,
  commission: IconCommission,
  customer: IconCustomers,
  history: IconHistory,
  hours: IconClock,
  online: IconGlobe,
  payment: IconHandCash,
  product: IconProduct,
  promotion: IconDiscount,
  report: IconReport,
  role: IconKey,
  staff: IconAttendance,
  treatment: IconTreatment,
};

export function Empty({ message = 'ไม่พบข้อมูล', icon = 'info' }) {
  const EmptyIcon = EMPTY_ICONS[icon] || IconInfo;
  return (
    <div className="empty">
      <div className="empty-icon" aria-hidden="true">
        <EmptyIcon size={30} />
      </div>
      <div>{message}</div>
    </div>
  );
}

export function ErrorNote({ error }) {
  if (!error) return null;
  return (
    <div className="login-error" style={{ marginBottom: 14 }}>
      {error.message || String(error)}
    </div>
  );
}

/**
 * Loads `fn` and re-runs it whenever `deps` change. Returns the payload plus a
 * `reload` so screens can refresh after a mutation without a full remount.
 */
export function useLoader(fn, deps = []) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Refetch when the branch switches, whatever this loader's own deps are.
  const [branchEpoch, setBranchEpoch] = useState(getBranchEpoch);
  useEffect(() => onBranchChange(setBranchEpoch), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fn()
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setError(e))
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce, branchEpoch]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}

export function Modal({ title, children, onClose, footer, wide, className }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  const titleId = useId();
  closeRef.current = onClose;

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector('button, input, select, textarea')?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeRef.current();
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  // Close on mousedown that both starts and ends on the backdrop. Using onClick
  // here would also fire for the very click that opened the dialog (the button
  // unmounts, the backdrop mounts under the cursor, and the click lands on it),
  // which made the first press of จองเลย appear to do nothing. It also stops a
  // text selection that ends outside the panel from closing it.
  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="modal-backdrop" onMouseDown={onBackdrop}>
      <div
        ref={dialogRef}
        className={`modal${wide ? ' wide' : ''}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-head">
          <span id={titleId}>{title}</span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}
